import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { debounce } from "lodash";
import L from "leaflet";
import { UserContext } from "../../UserContext";

// This component uses backend proxy endpoints for Google Places API
// The API key is stored securely on the backend server

const CustomGeoSearch = () => {
  const { t } = useTranslation();
  const map = useMap();
  const { token } = useContext(UserContext);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const searchContainerRef = useRef(null);

  // This function fetches location suggestions from backend proxy
  const fetchSuggestions = async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }

    try {
      // Using backend proxy endpoint instead of direct Google API call
      const response = await axios.get("/api/places/autocomplete", {
        params: {
          input: searchQuery,
          language: t("language")
        },
        headers: {
          Authorization: token
        }
      });

      if (response.data.predictions) {
        setResults(response.data.predictions);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      // Handle error, maybe show a toast notification
    }
  };

  // Debounce the fetch function to avoid excessive API calls
  const debouncedFetch = useCallback(debounce(fetchSuggestions, 400), []);
  useEffect(() => {
    debouncedFetch(query);
  }, [query, debouncedFetch]);

  // This function gets the full details (lat/lng) for a selected place
  const handleSelectResult = async (placeId) => {
    setQuery(""); // Clear input
    setResults([]); // Clear results

    try {
      // Using backend proxy endpoint instead of direct Google API call
      const response = await axios.get("/api/places/details", {
        params: {
          placeId: placeId
        },
        headers: {
          Authorization: token
        }
      });

      const location = response.data.result?.geometry?.location;
      if (location) {
        const { lat, lng } = location;
        map.flyTo([lat, lng], 16, { animate: true, duration: 1.5 });
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  useEffect(() => {
    const div = searchContainerRef.current;
    if (div) {
      // This Leaflet utility prevents click events from propagating to the map
      L.DomEvent.disableClickPropagation(div);
      // Also prevent scroll events from zooming the map
      L.DomEvent.disableScrollPropagation(div);
    }
  }, []);

  return (
    <div className="custom-geosearch-container" ref={searchContainerRef}>
      <div className="custom-geosearch-input-wrapper">
        <input
          type="text"
          className="custom-geosearch-input"
          placeholder={t("Search for a location...")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button
            className="custom-geosearch-clear-button"
            onClick={() => {
              setQuery("");
              setResults([]);
            }}
          >
            &times;
          </button>
        )}
      </div>

      {results.length > 0 && (
        <ul className="custom-geosearch-results-list">
          {results.map((result) => (
            <li
              key={result.place_id}
              className="custom-geosearch-result-item"
              onClick={() => handleSelectResult(result.place_id)}
            >
              <strong>{result.structured_formatting.main_text}</strong>
              <span>{result.structured_formatting.secondary_text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomGeoSearch;
